import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Clock, Video, MapPin, User, CheckCircle2, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicBooking() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState("type"); // type -> when -> where -> details -> done
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [location, setLocation] = useState(null);
  const [clientLocation, setClientLocation] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "" });
  const [monthOffset, setMonthOffset] = useState(0);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/scheduler/${slug}`).then((r) => setInfo(r.data)).catch(() => toast.error("Agent not found"));
  }, [slug]);

  const timeSlots = useMemo(() => {
    if (!selectedType || !selectedDate || !info?.availability) return [];
    const dayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][selectedDate.getDay()];
    const hours = info.availability.working_hours?.[dayKey];
    if (!hours?.enabled) return [];
    const [sh, sm] = hours.start.split(":").map(Number);
    const [eh, em] = hours.end.split(":").map(Number);
    const dur = selectedType.duration_minutes;
    const slots = [];
    for (let mins = sh * 60 + sm; mins + dur <= eh * 60 + em; mins += dur + (info.availability.buffer_minutes || 0)) {
      const h = Math.floor(mins / 60), m = mins % 60;
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
    return slots;
  }, [selectedType, selectedDate, info]);

  const submit = async () => {
    const start = new Date(selectedDate);
    const [h, m] = selectedTime.split(":").map(Number);
    start.setHours(h, m, 0, 0);
    const loc = location.type === "client" ? { ...location, address: clientLocation } : location;
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/scheduler/${slug}/book`, {
        meeting_type_id: selectedType.id,
        start_at: start.toISOString(),
        location: loc,
        ...form,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setBooked(data);
      setStep("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    }
  };

  if (!info) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;

  const monthDate = new Date();
  monthDate.setMonth(monthDate.getMonth() + monthOffset);
  const days = getMonthGrid(monthDate);

  return (
    <div className="min-h-screen bg-background grain">
      <Toaster theme="dark" richColors position="top-right" />
      <div className="border-b border-border py-4 px-6 backdrop-blur-xl bg-background/80 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm bg-primary flex items-center justify-center"><Zap className="h-4 w-4 text-white" /></div>
          <div className="font-display font-extrabold">LeadifyCRM</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center text-2xl font-bold">{info.user.name[0]}</div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold">Book with</div>
            <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">{info.user.name}</h1>
          </div>
        </div>

        {step === "type" && (
          <Card className="rounded-sm bg-card border-border p-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">1. Choose meeting type</div>
            <div className="grid gap-3">
              {info.types.length === 0 && <div className="text-muted-foreground">No meeting types configured yet.</div>}
              {info.types.map((t) => (
                <button key={t.id} onClick={() => { setSelectedType(t); setStep("when"); }} className="p-5 rounded-sm border border-border hover:border-primary bg-secondary/20 hover:bg-secondary/40 text-left transition-all group">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display text-xl font-extrabold">{t.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t.description || "No description"}</div>
                    </div>
                    <Badge variant="outline" className="rounded-sm"><Clock className="h-3 w-3 mr-1" />{t.duration_minutes} min</Badge>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        )}

        {step === "when" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-sm bg-card border-border p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">2. Pick a date</div>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setMonthOffset(monthOffset - 1)} className="p-1 rounded-sm hover:bg-secondary/40"><ChevronLeft className="h-4 w-4" /></button>
                <div className="font-display font-extrabold">{monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
                <button onClick={() => setMonthOffset(monthOffset + 1)} className="p-1 rounded-sm hover:bg-secondary/40"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => {
                  const isPast = d && d < new Date(new Date().setHours(0, 0, 0, 0));
                  const dayKey = d ? ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][d.getDay()] : null;
                  const disabled = !d || isPast || !info.availability?.working_hours?.[dayKey]?.enabled;
                  const isSelected = d && selectedDate && d.toDateString() === selectedDate.toDateString();
                  return (
                    <button key={i} disabled={disabled} onClick={() => { setSelectedDate(d); setSelectedTime(null); }}
                      className={`aspect-square rounded-sm text-sm ${disabled ? "text-muted-foreground/30 cursor-not-allowed" : isSelected ? "bg-primary text-white font-bold" : "hover:bg-secondary/60"}`}>
                      {d?.getDate() || ""}
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card className="rounded-sm bg-card border-border p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">3. Pick a time</div>
              {!selectedDate && <div className="text-sm text-muted-foreground">Select a date first.</div>}
              <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                {timeSlots.map((t) => (
                  <button key={t} onClick={() => { setSelectedTime(t); setStep("where"); }}
                    className={`p-2 rounded-sm border text-sm ${selectedTime === t ? "bg-primary text-white border-primary" : "border-border hover:border-primary/60 hover:bg-secondary/40"}`}>{t}</button>
                ))}
                {selectedDate && timeSlots.length === 0 && <div className="col-span-full text-sm text-muted-foreground">No slots on this day.</div>}
              </div>
            </Card>
          </div>
        )}

        {step === "where" && (
          <Card className="rounded-sm bg-card border-border p-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">4. Choose location</div>
            <div className="grid gap-3">
              {(selectedType.locations || []).map((l, i) => (
                <button key={i} onClick={() => setLocation(l)} className={`p-4 rounded-sm border text-left ${location?.label === l.label ? "border-primary bg-primary/10" : "border-border hover:border-primary/60 bg-secondary/20"}`}>
                  <div className="flex items-center gap-3">
                    {l.type === "online" ? <Video className="h-4 w-4 text-primary" /> : l.type === "client" ? <User className="h-4 w-4 text-primary" /> : <MapPin className="h-4 w-4 text-primary" />}
                    <div className="font-medium">{l.label}</div>
                  </div>
                  {l.address && <div className="text-xs text-muted-foreground mt-1 ml-7">{l.address}</div>}
                </button>
              ))}
            </div>
            {location?.type === "client" && (
              <div className="mt-4">
                <Label>Suggest a location</Label>
                <Input className="rounded-sm mt-1.5 bg-secondary/40" value={clientLocation} onChange={(e) => setClientLocation(e.target.value)} placeholder="Address / coffee shop / etc." />
              </div>
            )}
            <Button className="mt-6 rounded-sm bg-primary w-full h-11" disabled={!location || (location.type === "client" && !clientLocation)} onClick={() => setStep("details")}>Continue →</Button>
          </Card>
        )}

        {step === "details" && (
          <Card className="rounded-sm bg-card border-border p-8 max-w-lg">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">5. Your details</div>
            <div className="space-y-3">
              <div><Label>Name</Label><Input required className="rounded-sm mt-1.5 bg-secondary/40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input required type="email" className="rounded-sm mt-1.5 bg-secondary/40" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Company</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <Button className="mt-6 rounded-sm bg-primary w-full h-11" disabled={!form.name || !form.email} onClick={submit}>Confirm Booking →</Button>
          </Card>
        )}

        {step === "done" && (
          <Card className="rounded-sm bg-card border-primary/40 border-2 p-12 text-center">
            <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl font-extrabold tracking-tight mb-2">You're all set.</h2>
            <p className="text-muted-foreground">A confirmation has been sent to <span className="text-white">{form.email}</span>.</p>
            <div className="mt-6 p-4 border border-border rounded-sm inline-block text-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Meeting</div>
              <div className="font-display text-lg font-extrabold">{selectedType.name}</div>
              <div className="text-sm mt-1">{new Date(booked?.start_at).toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-1">{location.label}{clientLocation ? ` — ${clientLocation}` : ""}</div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function getMonthGrid(date) {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const arr = [];
  for (let i = 0; i < startDay; i++) arr.push(null);
  for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(y, m, d));
  while (arr.length % 7 !== 0) arr.push(null);
  return arr;
}
